import type { LocalizedCopy } from "../productCopyTypes";

function paragraphs(text: string): string {
  return text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>\n${p}\n</p>`)
    .join("\n\n");
}

function featureList(items: string[]): string {
  return `<ul>\n${items.map((item) => `  <li>${item}</li>`).join("\n")}\n</ul>`;
}

const COPY = {
  en: {
    title: "USS Enterprise CV-6",
    description:
      "USS Enterprise CV-6 is a large-scale brick-built set dedicated to one of the most famous aircraft carriers in naval history. With 4,269+ pieces and a 1:300 scale, the set combines an impressive display size with a high level of construction detail, making it particularly appealing to collectors of naval and military-themed brick sets.\n\nThe set reproduces the characteristic appearance of the Yorktown-class aircraft carrier, including its long flight deck, island superstructure, hull, deck equipment, aircraft, antennas, and numerous small structural details. The extensive use of precision pieces gives the completed carrier a highly detailed appearance, especially across the flight deck and upper structures.\n\nA major highlight is the modular construction system. Major sections can be separated during assembly, providing easier access to the interior structure and making the large set more manageable to build. The detailed upper deck and hull construction create a strong sense of depth, while the included aircraft add scale and visual character to the carrier's flight deck.\n\nThe finished set is designed primarily as a premium display piece. It is presented on a dedicated display stand with an identification plaque, while the long, low silhouette of the carrier makes it particularly effective as a centerpiece in a military, naval, or aircraft collection.\n\nWith a length of approximately 82.7 cm, the USS Enterprise CV-6 is a substantial display set. Its combination of scale, precision construction, historical significance, and extensive detailing makes it one of the most impressive naval sets in the collection.",
    highlightsTitle: "Product Features",
    features: [
      "Brand: 12GO",
      "Set Number: 98016",
      "Name: USS Enterprise CV-6",
      "Pieces: 4,269+",
      "Scale: 1:300",
      "Length: approx. 82.7 cm",
      "Height: approx. 26.4 cm",
      "Width: approx. 9.8 cm",
      "Large-scale aircraft carrier construction",
      "Precision brick elements",
      "Detailed flight deck",
      "Detailed island superstructure",
      "Multiple aircraft included",
      "Modular quick-release construction",
      "Removable structural sections",
      "Detailed hull and deck construction",
      "Display stand included",
      "Collector identification plaque",
      "Designed for display and collection",
    ],
    specsTitle: "Specifications",
    specs: [
      "<li><strong>Brand:</strong> 12GO</li>",
      "<li><strong>Set Number:</strong> 98016</li>",
      "<li><strong>Name:</strong> USS Enterprise CV-6</li>",
      "<li><strong>Pieces:</strong> 4,269+</li>",
      "<li><strong>Scale:</strong> 1:300</li>",
      "<li><strong>Dimensions:</strong> approx. 82.7 × 9.8 × 26.4 cm</li>",
    ],
  },
  pl: {
    title: "USS Enterprise CV-6",
    description:
      "USS Enterprise CV-6 to wielkoformatowy set z klocków poświęcony jednemu z najbardziej rozpoznawalnych lotniskowców w historii wojskowości. Zestaw składa się z ponad 4269 elementów i wykonany jest w skali 1:300, łącząc imponujące rozmiary z bardzo wysokim poziomem szczegółowości.\n\nKonstrukcja odwzorowuje charakterystyczną sylwetkę lotniskowca typu Yorktown, w tym rozległy pokład lotniczy, nadbudówkę wyspową, kadłub, wyposażenie pokładowe, samoloty, anteny oraz liczne drobne elementy konstrukcyjne. Zastosowanie precyzyjnych elementów pozwala uzyskać szczegółowy wygląd zarówno pokładu, jak i nadbudówki.\n\nJedną z najważniejszych cech jest modułowa konstrukcja. Poszczególne sekcje można rozdzielać podczas budowy, co ułatwia pracę z dużym setem i pozwala lepiej zobaczyć jego wewnętrzną strukturę. Szczegółowy pokład lotniczy oraz konstrukcja kadłuba nadają całości dużej głębi, a dołączone samoloty dodatkowo podkreślają skalę lotniskowca.\n\nGotowy set został zaprojektowany przede wszystkim z myślą o ekspozycji kolekcjonerskiej. W zestawie znajduje się dedykowana podstawka ekspozycyjna z tabliczką identyfikacyjną. Długa, charakterystyczna sylwetka sprawia, że lotniskowiec może pełnić funkcję centralnego elementu kolekcji militarnej lub marynistycznej.\n\nPrzy długości około 82,7 cm USS Enterprise CV-6 jest imponującym setem ekspozycyjnym. Połączenie dużej skali, precyzyjnej konstrukcji, historycznego znaczenia i licznych detali czyni go wyjątkową propozycją dla kolekcjonerów.",
    highlightsTitle: "Cechy produktu",
    features: [
      "Marka: 12GO",
      "Numer zestawu: 98016",
      "Nazwa: USS Enterprise CV-6",
      "Liczba elementów: 4269+",
      "Skala: 1:300",
      "Długość: ok. 82,7 cm",
      "Wysokość: ok. 26,4 cm",
      "Szerokość: ok. 9,8 cm",
      "Wielkoformatowa konstrukcja lotniskowca",
      "Precyzyjne elementy konstrukcyjne",
      "Szczegółowy pokład lotniczy",
      "Szczegółowa nadbudówka wyspowa",
      "Dołączone samoloty",
      "Modułowa konstrukcja typu quick-release",
      "Zdejmowane sekcje konstrukcji",
      "Szczegółowy kadłub i pokład",
      "Podstawka ekspozycyjna",
      "Tabliczka identyfikacyjna",
      "Przeznaczony do ekspozycji i kolekcjonowania",
    ],
    specsTitle: "Specyfikacja",
    specs: [
      "<li><strong>Marka:</strong> 12GO</li>",
      "<li><strong>Numer zestawu:</strong> 98016</li>",
      "<li><strong>Nazwa:</strong> USS Enterprise CV-6</li>",
      "<li><strong>Liczba elementów:</strong> 4269+</li>",
      "<li><strong>Skala:</strong> 1:300</li>",
      "<li><strong>Wymiary:</strong> ok. 82,7 × 9,8 × 26,4 cm</li>",
    ],
  },
  de: {
    title: "USS Enterprise CV-6",
    description:
      "USS Enterprise CV-6 ist ein großformatiges Klemmbaustein-Set, das einem der bekanntesten Flugzeugträger der Militärgeschichte gewidmet ist. Mit 4.269+ Teilen und einem Maßstab von 1:300 verbindet das Set eine beeindruckende Größe mit einer hohen Detaildichte.\n\nDie Konstruktion bildet die charakteristische Silhouette eines Yorktown-Klasse-Flugzeugträgers nach, einschließlich des langen Flugdecks, der Inselaufbauten, des Rumpfes, der Deckausrüstung, Flugzeuge, Antennen und zahlreicher kleiner Strukturelemente.\n\nEin besonderes Merkmal ist die modulare Konstruktion. Größere Sektionen können während des Aufbaus voneinander getrennt werden, wodurch das Arbeiten mit dem großen Set erleichtert und die innere Struktur zugänglicher wird. Die detaillierte Flugdeckgestaltung und die integrierten Flugzeuge verstärken den Gesamteindruck.\n\nDas fertige Set ist vor allem als hochwertiges Ausstellungsstück ausgelegt. Die enthaltene Präsentationsbasis mit Identifikationsplakette unterstreicht den Sammlercharakter.\n\nMit einer Länge von etwa 82,7 cm besitzt die USS Enterprise CV-6 eine beeindruckende Präsenz und eignet sich hervorragend als Mittelpunkt einer Marine- oder Militärsammlung.",
    highlightsTitle: "Produkteigenschaften",
    features: [
      "Marke: 12GO",
      "Setnummer: 98016",
      "Name: USS Enterprise CV-6",
      "Teile: 4.269+",
      "Maßstab: 1:300",
      "Länge: ca. 82,7 cm",
      "Höhe: ca. 26,4 cm",
      "Breite: ca. 9,8 cm",
      "Großformatiger Flugzeugträger",
      "Präzise Bauelemente",
      "Detailliertes Flugdeck",
      "Detaillierte Inselaufbauten",
      "Mehrere Flugzeuge enthalten",
      "Modulare Konstruktion",
      "Abnehmbare Konstruktionssektionen",
      "Detaillierter Rumpf",
      "Präsentationsständer",
      "Identifikationsplakette",
      "Für Ausstellung und Sammlung geeignet",
    ],
    specsTitle: "Spezifikationen",
    specs: [
      "<li><strong>Marke:</strong> 12GO</li>",
      "<li><strong>Setnummer:</strong> 98016</li>",
      "<li><strong>Name:</strong> USS Enterprise CV-6</li>",
      "<li><strong>Teile:</strong> 4.269+</li>",
      "<li><strong>Maßstab:</strong> 1:300</li>",
      "<li><strong>Abmessungen:</strong> ca. 82,7 × 9,8 × 26,4 cm</li>",
    ],
  },
  fr: {
    title: "USS Enterprise CV-6",
    description:
      "USS Enterprise CV-6 est un set en briques de grande taille consacré à l'un des porte-avions les plus célèbres de l'histoire navale. Avec plus de 4 269 pièces et une échelle 1:300, il combine une taille impressionnante et un niveau de détail particulièrement élevé.\n\nLa construction reproduit les lignes caractéristiques d'un porte-avions de la classe Yorktown, avec son long pont d'envol, son îlot, sa coque, ses équipements de pont, ses avions et ses nombreux petits détails structurels.\n\nLa construction modulaire constitue l'un des points forts du set. Plusieurs grandes sections peuvent être séparées pendant le montage, ce qui facilite la construction et permet de mieux observer la structure interne.\n\nLe set final est conçu comme une pièce d'exposition haut de gamme, avec un support dédié et une plaque d'identification. Avec une longueur d'environ 82,7 cm, il possède une forte présence visuelle et peut devenir la pièce centrale d'une collection navale ou militaire.",
    highlightsTitle: "Caractéristiques",
    features: [
      "Marque : 12GO",
      "Référence : 98016",
      "Nom : USS Enterprise CV-6",
      "Nombre de pièces : 4 269+",
      "Échelle : 1:300",
      "Longueur : env. 82,7 cm",
      "Hauteur : env. 26,4 cm",
      "Largeur : env. 9,8 cm",
      "Construction de grande taille",
      "Éléments de précision",
      "Pont d'envol détaillé",
      "Îlot détaillé",
      "Avions inclus",
      "Construction modulaire",
      "Sections amovibles",
      "Coque détaillée",
      "Support d'exposition",
      "Plaque d'identification",
      "Conçu pour exposition et collection",
    ],
    specsTitle: "Spécifications",
    specs: [
      "<li><strong>Marque :</strong> 12GO</li>",
      "<li><strong>Référence :</strong> 98016</li>",
      "<li><strong>Nom :</strong> USS Enterprise CV-6</li>",
      "<li><strong>Nombre de pièces :</strong> 4 269+</li>",
      "<li><strong>Échelle :</strong> 1:300</li>",
      "<li><strong>Dimensions :</strong> env. 82,7 × 9,8 × 26,4 cm</li>",
    ],
  },
  es: {
    title: "USS Enterprise CV-6",
    description:
      "USS Enterprise CV-6 es un set de bloques de gran formato dedicado a uno de los portaaviones más famosos de la historia naval. Con más de 4.269 piezas y una escala 1:300, combina un tamaño impresionante con un alto nivel de detalle.\n\nLa construcción reproduce las características de un portaaviones de la clase Yorktown, incluyendo la gran cubierta de vuelo, la superestructura de isla, el casco, el equipamiento de cubierta, las aeronaves y numerosos detalles estructurales.\n\nUna de sus principales características es la construcción modular. Varias secciones pueden separarse durante el montaje, facilitando el trabajo con un set de estas dimensiones y permitiendo apreciar mejor su estructura interna.\n\nEl set está diseñado principalmente como una pieza de exposición para coleccionistas. Incluye una base de exposición y una placa identificativa. Con una longitud de aproximadamente 82,7 cm, tiene una gran presencia visual.",
    highlightsTitle: "Características",
    features: [
      "Marca: 12GO",
      "Número de set: 98016",
      "Nombre: USS Enterprise CV-6",
      "Piezas: 4.269+",
      "Escala: 1:300",
      "Longitud: aprox. 82,7 cm",
      "Altura: aprox. 26,4 cm",
      "Ancho: aprox. 9,8 cm",
      "Construcción de gran formato",
      "Piezas de precisión",
      "Cubierta de vuelo detallada",
      "Superestructura detallada",
      "Aeronaves incluidas",
      "Construcción modular",
      "Secciones desmontables",
      "Casco detallado",
      "Base de exposición",
      "Placa identificativa",
      "Diseñado para exposición y colección",
    ],
    specsTitle: "Especificaciones",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Número de set:</strong> 98016</li>",
      "<li><strong>Nombre:</strong> USS Enterprise CV-6</li>",
      "<li><strong>Piezas:</strong> 4.269+</li>",
      "<li><strong>Escala:</strong> 1:300</li>",
      "<li><strong>Dimensiones:</strong> aprox. 82,7 × 9,8 × 26,4 cm</li>",
    ],
  },
  nl: {
    title: "USS Enterprise CV-6",
    description:
      "USS Enterprise CV-6 is een grootformaat bouwstenenmodel van een van de bekendste vliegdekschepen uit de maritieme geschiedenis. Met 4.269+ onderdelen en een schaal van 1:300 combineert het model een indrukwekkende afmeting met een hoge mate van detail.\n\nDe constructie is gebaseerd op het herkenbare ontwerp van een vliegdekschip uit de Yorktown-klasse, met een uitgebreid vliegdek, eilandopbouw, romp, dekuitrusting, vliegtuigen, antennes en vele kleine constructiedetails.\n\nEen belangrijk onderdeel is de modulaire constructie. Grote secties kunnen tijdens het bouwen van elkaar worden losgemaakt, waardoor het grote project beter hanteerbaar wordt en de interne opbouw beter zichtbaar is.\n\nHet voltooide set is ontworpen als een hoogwaardig displaystuk en wordt geleverd met een displaystandaard en identificatieplaatje. Met een lengte van ongeveer 82,7 cm heeft het een sterke aanwezigheid in een collectie.",
    highlightsTitle: "Productkenmerken",
    features: [
      "Merk: 12GO",
      "Setnummer: 98016",
      "Naam: USS Enterprise CV-6",
      "Onderdelen: 4.269+",
      "Schaal: 1:300",
      "Lengte: ca. 82,7 cm",
      "Hoogte: ca. 26,4 cm",
      "Breedte: ca. 9,8 cm",
      "Grootformaat vliegdekschip",
      "Precisieonderdelen",
      "Gedetailleerd vliegdek",
      "Gedetailleerde eilandopbouw",
      "Vliegtuigen inbegrepen",
      "Modulaire constructie",
      "Afneembare secties",
      "Gedetailleerde romp",
      "Displaystandaard",
      "Identificatieplaatje",
      "Voor display en collectie",
    ],
    specsTitle: "Specificaties",
    specs: [
      "<li><strong>Merk:</strong> 12GO</li>",
      "<li><strong>Setnummer:</strong> 98016</li>",
      "<li><strong>Naam:</strong> USS Enterprise CV-6</li>",
      "<li><strong>Onderdelen:</strong> 4.269+</li>",
      "<li><strong>Schaal:</strong> 1:300</li>",
      "<li><strong>Afmetingen:</strong> ca. 82,7 × 9,8 × 26,4 cm</li>",
    ],
  },
  it: {
    title: "USS Enterprise CV-6",
    description:
      "USS Enterprise CV-6 è un set di grandi dimensioni in mattoncini dedicato a una delle più famose portaerei della storia navale. Con 4.269+ pezzi e una scala 1:300, combina dimensioni imponenti e un elevato livello di dettaglio.\n\nLa costruzione riproduce le caratteristiche di una portaerei della classe Yorktown, compresi l'ampio ponte di volo, la sovrastruttura a isola, lo scafo, le attrezzature di bordo, gli aeromobili e numerosi piccoli dettagli strutturali.\n\nLa costruzione modulare permette di separare diverse sezioni durante il montaggio, rendendo più semplice la realizzazione del grande set e permettendo di osservare meglio la struttura.\n\nIl set è pensato soprattutto come pezzo da esposizione per collezionisti e include una base dedicata con targhetta identificativa. Con una lunghezza di circa 82,7 cm, rappresenta un elemento centrale per una collezione navale o militare.",
    highlightsTitle: "Caratteristiche",
    features: [
      "Marca: 12GO",
      "Numero set: 98016",
      "Nome: USS Enterprise CV-6",
      "Pezzi: 4.269+",
      "Scala: 1:300",
      "Lunghezza: circa 82,7 cm",
      "Altezza: circa 26,4 cm",
      "Larghezza: circa 9,8 cm",
      "Costruzione di grandi dimensioni",
      "Elementi di precisione",
      "Ponte di volo dettagliato",
      "Sovrastruttura dettagliata",
      "Aerei inclusi",
      "Costruzione modulare",
      "Sezioni removibili",
      "Scafo dettagliato",
      "Base espositiva",
      "Targhetta identificativa",
      "Ideale per esposizione e collezione",
    ],
    specsTitle: "Specifiche",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Numero set:</strong> 98016</li>",
      "<li><strong>Nome:</strong> USS Enterprise CV-6</li>",
      "<li><strong>Pezzi:</strong> 4.269+</li>",
      "<li><strong>Scala:</strong> 1:300</li>",
      "<li><strong>Dimensioni:</strong> circa 82,7 × 9,8 × 26,4 cm</li>",
    ],
  },
  ru: {
    title: "USS Enterprise CV-6",
    description:
      "USS Enterprise CV-6 — крупномасштабный набор из конструктора, посвящённый одному из самых известных авианосцев в военно-морской истории. Набор содержит 4269+ деталей и выполнен в масштабе 1:300, сочетая большие размеры с высокой степенью детализации.\n\nКонструкция воспроизводит характерные особенности авианосца типа Yorktown, включая полётную палубу, островную надстройку, корпус, палубное оборудование, самолёты, антенны и многочисленные мелкие элементы.\n\nОдним из главных достоинств является модульная конструкция. Крупные секции можно разъединять во время сборки, что облегчает работу с большим набором и позволяет лучше рассмотреть его внутреннюю структуру.\n\nГотовый набор предназначен прежде всего для коллекционной экспозиции. В комплект входит специальная подставка с идентификационной табличкой. При длине около 82,7 см авианосец способен стать центральным элементом военной или военно-морской коллекции.",
    highlightsTitle: "Характеристики",
    features: [
      "Бренд: 12GO",
      "Номер набора: 98016",
      "Название: USS Enterprise CV-6",
      "Количество деталей: 4269+",
      "Масштаб: 1:300",
      "Длина: около 82,7 см",
      "Высота: около 26,4 см",
      "Ширина: около 9,8 см",
      "Крупномасштабная конструкция авианосца",
      "Высокоточные детали",
      "Детализированная полётная палуба",
      "Детализированная надстройка",
      "Самолёты в комплекте",
      "Модульная конструкция",
      "Съёмные секции",
      "Детализированный корпус",
      "Подставка для экспонирования",
      "Идентификационная табличка",
      "Для экспонирования и коллекционирования",
    ],
    specsTitle: "Спецификация",
    specs: [
      "<li><strong>Бренд:</strong> 12GO</li>",
      "<li><strong>Номер набора:</strong> 98016</li>",
      "<li><strong>Название:</strong> USS Enterprise CV-6</li>",
      "<li><strong>Количество деталей:</strong> 4269+</li>",
      "<li><strong>Масштаб:</strong> 1:300</li>",
      "<li><strong>Размеры:</strong> около 82,7 × 9,8 × 26,4 см</li>",
    ],
  },
  zh: {
    title: "USS Enterprise CV-6",
    description:
      "USS Enterprise CV-6 是一款大型航空母舰积木套装，致敬海军历史上最具代表性的航空母舰之一。整套包含 4269+ 个零件，采用 1:300 比例，在保持庞大尺寸的同时，也加入了丰富的精细结构。\n\n模型重点还原了 Yorktown 级航空母舰的经典特征，包括宽大的飞行甲板、舰岛、舰体、甲板设备、舰载机、天线以及大量细节结构。\n\n套装采用模块化结构设计，部分大型结构可以在拼搭过程中进行拆分，使大型套装更加方便组装，也能够更清晰地观察内部结构。\n\n完成后的套装定位于收藏与展示，配备专用展示底座以及身份铭牌。成品长度约 82.7 cm，非常适合作为军事、海军或航空主题收藏中的核心展示作品。",
    highlightsTitle: "产品特点",
    features: [
      "品牌： 12GO",
      "产品编号： 98016",
      "名称： USS Enterprise CV-6",
      "积木数量： 4269+ 块",
      "比例： 1:300",
      "长度： 约 82.7 cm",
      "高度： 约 26.4 cm",
      "宽度： 约 9.8 cm",
      "大型航空母舰结构",
      "高精度积木零件",
      "精细飞行甲板",
      "精细舰岛结构",
      "包含多架舰载机",
      "模块化结构",
      "可拆卸结构组件",
      "精细舰体",
      "专用展示底座",
      "身份铭牌",
      "适合展示与收藏",
    ],
    specsTitle: "规格",
    specs: [
      "<li><strong>品牌：</strong> 12GO</li>",
      "<li><strong>产品编号：</strong> 98016</li>",
      "<li><strong>名称：</strong> USS Enterprise CV-6</li>",
      "<li><strong>积木数量：</strong> 4269+ 块</li>",
      "<li><strong>比例：</strong> 1:300</li>",
      "<li><strong>成品尺寸：</strong> 约 82.7 × 9.8 × 26.4 cm</li>",
    ],
  },
};

type LocaleKey = keyof typeof COPY;

function buildLocaleHtml(locale: LocaleKey): string {
  const c = COPY[locale] ?? COPY.en;
  return `<h1>${c.title}</h1>

${paragraphs(c.description)}

<h2>${c.specsTitle}</h2>

<ul>
${c.specs.join("\n")}
</ul>

<h2>${c.highlightsTitle}</h2>

${featureList([...c.features])}`;
}

export const RICH_PRODUCT_COPY_98016: LocalizedCopy = {
  en: buildLocaleHtml("en"),
  pl: buildLocaleHtml("pl"),
  de: buildLocaleHtml("de"),
  es: buildLocaleHtml("es"),
  fr: buildLocaleHtml("fr"),
  nl: buildLocaleHtml("nl"),
  zh: buildLocaleHtml("zh"),
  it: buildLocaleHtml("it"),
  ru: buildLocaleHtml("ru"),
};
